import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom'

// ICONS
import { PanelLeftClose, DoorOpen, ChevronLeft } from 'lucide-react'

// ANIMATION
import { motion, AnimatePresence } from 'framer-motion'

// IMAGES
import LogoMobile from 'assets/images/Pastille_AI_RVB_Orange.png';

// CONSTANTS
import menuItems from 'constants/menu';

// CONTEXT
import { useMenu } from 'context/MenuContext';
import { useConnection } from 'context/ConnectionContext';
import useUserData from 'hooks/useUserData';
import { AuthContext } from 'context/AuthContext';

const SidebarMenu = ({ isSidebarOpen, setIsSidebarOpen }) => {
    const [openMenus, setOpenMenus] = useState({})
    const location = useLocation()
    const navigate = useNavigate()
    const pathname = location.pathname
    const { pendingProposals } = useMenu();
    const { setHasInsertedConnection } = useConnection();
    const { email, isInternal, isExternal, isAdmin } = React.useContext(AuthContext);
    const { isSuperAdmin } = useUserData(email, isInternal, isExternal, isAdmin);

    useEffect(() => {
        if (window.innerWidth < 768) {
            setTimeout(() => {
                setIsSidebarOpen(false)
            }, 500)
        }
    }, [pathname])

    useEffect(() => {
        const newOpenMenus = {}
        menuItems.forEach((item) => {
            if (item.children?.some((child) => pathname.startsWith(child.path))) {
                newOpenMenus[item.path] = true
            }
        })
        setOpenMenus(newOpenMenus)
    }, [pathname])

    const toggleMenu = (path) => {
        setOpenMenus({
            [path]: !(openMenus[path] ?? false),
        })
    }

    return (
        <aside className="sidebar w-80 h-[calc(100vh-80px)] md:h-[100vh] flex flex-col bg-blanc dark:bg-dark fixed z-10">
            {/* Header */}
            <div className="px-2 py-4 flex items-center justify-between">                
                <div className="flex items-center justify-start ml-4">
                    {/* Logo */}
                    <img src={LogoMobile} alt="Logo" className="size-10" />
                    {/* Title */}
                    <h1 className="text-noir dark:text-blanc text-2xl font-bold ml-4">Administration</h1>
                </div>
                <div className="flex-1 flex justify-end">
                    {/* Close sidebar button */}
                    <button
                        onClick={() => setIsSidebarOpen(false)}
                        className="p-2 rounded-lg hover:bg-noir/10 dark:hover:bg-gris/40 cursor-pointer"
                    >
                        <PanelLeftClose
                            className="text-noir dark:text-blanc"
                            size={24}
                        />
                    </button>
                </div>
            </div>

            {/* Scrollable menu */}
            <div className="flex-1 overflow-auto px-2">
                <nav className="flex flex-col gap-2">
                    {menuItems.map((item) => {
                        // Filtrer les éléments du menu en fonction du statut super admin
                        const filteredChildren = item.children?.filter(child => 
                            !child.requiresSuperAdmin || (child.requiresSuperAdmin && isSuperAdmin)
                        );

                        // Ne pas afficher les éléments parents qui n'ont plus d'enfants après filtrage
                        if (item.children && filteredChildren.length === 0) {
                            return null;
                        }

                        const Icon = item.icon
                        const hasChildren = filteredChildren?.length > 0
                        const isOpen = openMenus[item.path] || false
                        const isExactMatch = pathname === item.path
                        const isSubItemActive = filteredChildren?.some(
                            (child) => pathname.startsWith(child.path)
                        )
                        const isParentActive = isExactMatch || isSubItemActive
                        const showNotification = item.path === '/admin/tiles' && pendingProposals > 0

                        return (
                            <div key={item.path} className="w-full">
                                {hasChildren ? (
                                    <button
                                        onClick={() => toggleMenu(item.path)}
                                        className={`w-full text-noir dark:text-blanc text-h5 px-5 py-2.5 flex items-center gap-5 rounded-lg hover:bg-noir/10 dark:hover:bg-gris/40 transition justify-between cursor-pointer ${
                                            isParentActive
                                                ? 'bg-noir/10 dark:bg-gris/40'
                                                : ''
                                        }`}
                                    >
                                        <div className="flex items-center gap-5">
                                            <div className="relative">
                                                <Icon
                                                    className="text-noir dark:text-blanc"
                                                    size={24}
                                                />
                                                {showNotification && (
                                                    <div className="absolute -top-1 -right-1 size-3 bg-red-500 rounded-full" />
                                                )}
                                            </div>
                                            {item.label}
                                        </div>
                                        <ChevronLeft
                                            size={24}
                                            className={`text-noir dark:text-blanc transform transition-transform duration-200 ${
                                                isOpen ? '-rotate-90' : ''
                                            }`}
                                        />
                                    </button>
                                ) : (
                                    <Link
                                        to={item.path}
                                        className={`w-full text-noir dark:text-blanc text-h5 px-5 py-2.5 flex items-center gap-5 rounded-lg hover:bg-noir/10 dark:hover:bg-gris/40 transition justify-between ${
                                            isExactMatch
                                                ? 'bg-noir/10 dark:bg-gris/40'
                                                : ''
                                        }`}
                                    >
                                        <div className="flex items-center gap-5">
                                            <Icon
                                                className="text-noir dark:text-blanc"
                                                size={24}
                                            />
                                            {item.label}
                                        </div>
                                    </Link>
                                )}

                                {/* Sous-menu */}
                                <AnimatePresence initial={false}>
                                    {hasChildren && isOpen && (
                                        <motion.div
                                            key="submenu"
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{
                                                duration: 0.3,
                                                ease: 'easeInOut',
                                            }}
                                            className="flex flex-col gap-2 mt-1 overflow-hidden"
                                        >
                                            {filteredChildren.map((subItem) => {
                                                const isSubActive =
                                                    pathname.startsWith(subItem.path)
                                                const showSubNotification = subItem.path === '/admin/tiles/proposals' && pendingProposals > 0

                                                return (
                                                    <Link
                                                        key={subItem.path}
                                                        to={subItem.path}
                                                        className={`self-stretch text-noir dark:text-blanc text-thin-s px-5 py-2.5 pl-16 inline-flex justify-start items-center rounded-lg hover:bg-noir/10 dark:hover:bg-gris/40 transition overflow-hidden ${
                                                            isSubActive
                                                                ? 'bg-noir/10 dark:bg-gris/40'
                                                                : ''
                                                        }`}
                                                    >
                                                        <div className="relative flex-1">
                                                            {showSubNotification && (
                                                                <span className="absolute -left-5 top-1/2 -translate-y-1/2 size-2 bg-red-500 rounded-full" />
                                                            )}
                                                            <span className="pl-4">{subItem.label}</span>
                                                        </div>
                                                    </Link>
                                                )
                                            })}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )
                    })}
                </nav>
            </div>

            {/* buttons fixe en bas */}
            <div className="py-4 px-2 w-full">
                <button
                    onClick={() => {
                        setHasInsertedConnection(false);
                        navigate('/', { replace: true });
                    }}
                    className="w-full text-noir dark:text-blanc text-h5 px-5 py-2.5 flex items-center gap-5 rounded-lg hover:bg-noir/10 dark:hover:bg-gris/40 transition justify-between cursor-pointer"
                >
                    <div className="flex items-center gap-5">
                        <DoorOpen
                            alt="Accueil"
                            className="text-noir dark:text-blanc"
                            size={24} 
                        />
                        Accueil
                    </div>
                </button>
            </div>
        </aside>
    );
};

export default SidebarMenu;